import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/api/response.types';

// Context: mirrors the server's resend window (boq-api-core VerificationResendThrottle) and is
// used only when a proxy has stripped Retry-After. The server, not this value, is the control.
export const DEFAULT_RETRY_AFTER_SECONDS = 30;

/** `meta.code` on the 403 sign-in returns when the password verified but the email has not. */
export const EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED';

/**
 * Determines whether an HTTP status code represents a successful response.
 *
 * Checks the full 2xx success range (200–299 inclusive)
 *
 * @param status - The HTTP status code from a response (e.g. `response.status`).
 * @returns `true` if {@link status} is in the range 200–299 inclusive,
 *   otherwise `false`. Non-finite values (`NaN`, `Infinity`) return `false`.
 */
export const isSuccess = (status: number): boolean => {
  return Number.isFinite(status) && status >= 200 && status < 300;
};

/** Machine-readable error code from the API's `meta.code`, where the endpoint sets one. */
export const errorCode = (error: unknown): string | null => {
  const meta = (error as AxiosError<ErrorResponse>)?.response?.data?.meta as
    | { code?: string }
    | undefined;

  return typeof meta?.code === 'string' ? meta.code : null;
};

/**
 * Seconds the caller must wait, read from a throttled response's `Retry-After`. Falls back to
 * {@link DEFAULT_RETRY_AFTER_SECONDS} when the header is missing or unusable, so a countdown
 * never renders as `0s` against a window the server is still holding open.
 */
export const retryAfterSeconds = (error: unknown): number => {
  const header = (error as AxiosError)?.response?.headers?.['retry-after'];
  const seconds = Number.parseInt(String(header ?? ''), 10);

  return Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_RETRY_AFTER_SECONDS;
};
